import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, Container, Typography, Chip, alpha } from '@mui/material';
import {
  Edit          as EditIcon,
  Delete        as DeleteIcon,
  Stars         as StarsIcon,
  AttachMoney   as MoneyIcon,
  Description   as DescriptionIcon,
  Visibility    as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  PhotoSizeSelectActual as LogoSizeIcon,
  Circle        as CircleIcon,
} from '@mui/icons-material';
import Header                  from '../../common/Header';
import Loading                 from '../../common/Loading';
import FeedbackMessage         from '../../common/FeedbackMessage';
import ConfirmDeleteDialog     from '../../common/ConfirmDeleteDialog';
import api                     from '../../../services/api';
import { injectDetailsPageStyles } from '../../common/DetailsPagePrimitives';
import SystemInformationSection from '../../common/SystemInformationSection';
import PartnerPublicPreview from './PartnerPublicPreview';

// ─── Design tokens ────────────────────────────────────────────────────────────
const NEON       = '#38BDF8';
const NEON2      = '#818CF8';
const NEON3      = '#34D399';
const DANGER     = '#F87171';
const SURFACE_MID = '#0a1628';
const SURFACE_UP  = '#0f1f38';
const SURFACE_TOP = '#152640';
const TEXT_BODY   = '#E2E8F0';
const TEXT_MUTED  = '#64748B';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const formatAuditUser = (tierRecord, field) => {
  if (field === 'created') {
    return (
      tierRecord.creator?.name ||
      tierRecord.created_by_name ||
      (tierRecord.created_by != null ? `User #${tierRecord.created_by}` : null)
    );
  }
  return (
    tierRecord.last_updater?.name ||
    tierRecord.updated_by_name ||
    (tierRecord.updated_by != null ? `User #${tierRecord.updated_by}` : null)
  );
};

// ─── Small reusable primitives ────────────────────────────────────────────────
const Card = ({ children, accent = NEON, delay = 0, sx = {} }) => (
  <Box sx={{
    borderRadius: '16px',
    border: `1px solid ${alpha(accent, 0.14)}`,
    background: `linear-gradient(160deg, ${alpha(SURFACE_UP, 0.8)} 0%, ${alpha(SURFACE_MID, 0.98)} 100%)`,
    overflow: 'hidden',
    animation: `fadeUp 0.4s ease ${delay}s both`,
    '@keyframes fadeUp': {
      from: { opacity: 0, transform: 'translateY(12px)' },
      to:   { opacity: 1, transform: 'translateY(0)' },
    },
    ...sx,
  }}>
    {children}
  </Box>
);

const CardHeader = ({ icon: Icon, title, accent = NEON }) => (
  <Box sx={{
    display: 'flex', alignItems: 'center', gap: 1.2,
    px: 2.5, py: 1.5,
    borderBottom: `1px solid ${alpha(accent, 0.1)}`,
    background: alpha(accent, 0.04),
  }}>
    <Box sx={{
      width: 26, height: 26, borderRadius: '7px',
      background: alpha(accent, 0.12),
      border: `1px solid ${alpha(accent, 0.22)}`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <Icon sx={{ fontSize: 13, color: accent }} />
    </Box>
    <Typography sx={{
      fontFamily: "'DM Sans', sans-serif", fontWeight: 700,
      fontSize: '0.78rem', color: alpha(TEXT_BODY, 0.85),
      letterSpacing: '0.07em', textTransform: 'uppercase',
    }}>
      {title}
    </Typography>
  </Box>
);

const FieldLabel = ({ children }) => (
  <Typography sx={{
    fontSize: '0.65rem', fontWeight: 600,
    color: TEXT_MUTED, letterSpacing: '0.07em',
    textTransform: 'uppercase', fontFamily: "'DM Sans', sans-serif",
    mb: 0.4,
  }}>
    {children}
  </Typography>
);

const FieldValue = ({ children, muted, sx = {} }) => (
  <Typography sx={{
    fontSize: '0.9rem', fontFamily: "'DM Sans', sans-serif",
    color: muted ? alpha(TEXT_MUTED, 0.6) : alpha(TEXT_BODY, 0.9),
    fontStyle: muted ? 'italic' : 'normal',
    fontWeight: muted ? 400 : 500,
    lineHeight: 1.6,
    ...sx,
  }}>
    {children}
  </Typography>
);

const VisibilityBadge = ({ visible, label }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
    {visible
      ? <VisibilityIcon sx={{ fontSize: 14, color: NEON3 }} />
      : <VisibilityOffIcon sx={{ fontSize: 14, color: alpha(DANGER, 0.8) }} />}
    <Typography sx={{
      fontSize: '0.85rem', fontWeight: 600,
      fontFamily: "'DM Sans', sans-serif",
      color: visible ? NEON3 : alpha(DANGER, 0.9),
    }}>
      {label}
    </Typography>
  </Box>
);

// ─── Main component ───────────────────────────────────────────────────────────
const SponsorTierDetails = () => {
  const { id }      = useParams();
  const navigate    = useNavigate();
  const [tier,              setTier]              = useState(null);
  const [loading,           setLoading]           = useState(true);
  const [error,             setError]             = useState(null);
  const [successMessage,    setSuccessMessage]    = useState('');
  const [deleteDialogOpen,  setDeleteDialogOpen]  = useState(false);
  const [deleteLoading,     setDeleteLoading]     = useState(false);

  useEffect(() => { injectDetailsPageStyles(); }, []);

  useEffect(() => {
    const fetchTier = async () => {
      try {
        setLoading(true);
        const { data } = await api.get(`/sponsor-tiers/${id}`);
        setTier(data);
      } catch (e) {
        setError(e.response?.data?.message || 'Failed to load sponsor tier details.');
      } finally {
        setLoading(false);
      }
    };
    fetchTier();
  }, [id]);

  const confirmDelete = async () => {
    if (!tier) return;
    try {
      setDeleteLoading(true);
      await api.delete(`/sponsor-tiers/${tier.id}`);
      setSuccessMessage(`Tier "${tier.tier_name}" deleted successfully.`);
      setTimeout(() => navigate('/sponsor-tiers', { state: { refreshList: Date.now() } }), 1200);
    } catch (e) {
      setError(e.response?.data?.message || 'Delete failed.');
    } finally {
      setDeleteLoading(false);
      setDeleteDialogOpen(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Loading message="Loading sponsor tier" size={56} variant="primary" />
      </Box>
    );
  }

  if (!tier) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <FeedbackMessage message={error || 'Sponsor tier not found'} severity="error" />
      </Container>
    );
  }

  const amount = tier.sponsor_amount != null
    ? `RM ${Number(tier.sponsor_amount).toLocaleString()}`
    : null;

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 6 }}>

      {/* ── Header ── */}
      <Header
        title="Sponsor Tier Details"
        subtitle="View sponsor tier information"
        icon={StarsIcon}
        showBackButton
        onBackClick={() => navigate('/sponsor-tiers')}
        actionButton={(
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button
              startIcon={<EditIcon sx={{ fontSize: 16 }} />}
              onClick={() => navigate(`/sponsor-tiers/edit/${id}`)}
              sx={{
                borderRadius: '10px', px: 2.5, py: 0.7,
                fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.85rem',
                background: 'transparent', border: `1.5px solid ${alpha(NEON, 0.6)}`,
                color: NEON, transition: 'all 0.2s ease',
                '&:hover': { background: alpha(NEON, 0.08), borderColor: NEON, boxShadow: `0 0 12px ${alpha(NEON, 0.2)}` },
              }}
            >
              Edit
            </Button>
            <Button
              startIcon={<DeleteIcon sx={{ fontSize: 16 }} />}
              onClick={() => setDeleteDialogOpen(true)}
              sx={{
                borderRadius: '10px', px: 2.5, py: 0.7,
                fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.85rem',
                background: 'transparent', border: `1.5px solid ${alpha(DANGER, 0.6)}`,
                color: DANGER, transition: 'all 0.2s ease',
                '&:hover': { background: alpha(DANGER, 0.08), borderColor: DANGER, boxShadow: `0 0 12px ${alpha(DANGER, 0.2)}` },
              }}
            >
              Delete
            </Button>
          </Box>
        )}
      />

      {/* ── Feedback ── */}
      <FeedbackMessage
        message={error}
        severity="error"
        onClose={() => setError(null)}
        sx={{
          fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', fontWeight: 500,
          background: alpha('#450a0a', 0.92), border: '1px solid rgba(248,113,113,0.3)',
          boxShadow: '0 4px 20px rgba(239,68,68,0.12)', color: '#fca5a5', mb: 2.5,
        }}
      />
      <FeedbackMessage
        message={successMessage}
        severity="success"
        onClose={() => setSuccessMessage('')}
        sx={{
          fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', fontWeight: 500,
          background: `linear-gradient(135deg, ${alpha(NEON3, 0.1)}, ${alpha(SURFACE_MID, 0.95)})`,
          border: `1px solid ${alpha(NEON3, 0.35)}`,
          boxShadow: `0 4px 20px ${alpha(NEON3, 0.15)}`,
          color: alpha(NEON3, 0.95), mb: 2.5,
        }}
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

        {/* ── Hero ── */}
        <Box sx={{
          borderRadius: '16px',
          border: `1px solid ${alpha(NEON, 0.15)}`,
          background: `linear-gradient(135deg, ${alpha(SURFACE_UP, 0.9)} 0%, ${alpha(SURFACE_MID, 0.98)} 100%)`,
          p: { xs: 2, md: 2.5 },
          animation: 'fadeUp 0.35s ease both',
          '@keyframes fadeUp': { from: { opacity: 0, transform: 'translateY(10px)' }, to: { opacity: 1, transform: 'translateY(0)' } },
        }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 2.5, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{
                width: 48, height: 48, borderRadius: '13px', flexShrink: 0,
                background: `linear-gradient(135deg, ${alpha(NEON2, 0.25)}, ${alpha(NEON, 0.15)})`,
                border: `2px solid ${alpha(NEON2, 0.3)}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <StarsIcon sx={{ fontSize: 22, color: NEON2 }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, fontFamily: "'Rajdhani', sans-serif", color: TEXT_BODY, letterSpacing: 0.3, lineHeight: 1.15 }}>
                  {tier.tier_name || 'Sponsor Tier'}
                </Typography>
                <Typography sx={{ fontSize: '0.78rem', color: TEXT_MUTED, fontFamily: "'DM Sans', sans-serif" }}>
                  Sponsorship tier configuration
                </Typography>
              </Box>
            </Box>

            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 0.7,
              px: 1.4, py: 0.5, borderRadius: '20px',
              background: alpha(NEON3, 0.1),
              border: `1px solid ${alpha(NEON3, 0.3)}`,
              alignSelf: 'flex-start',
            }}>
              <CircleIcon sx={{ fontSize: 7, color: NEON3 }} />
              <Typography sx={{ fontSize: '0.75rem', fontFamily: "'DM Sans', sans-serif", fontWeight: 700, color: NEON3, letterSpacing: '0.05em' }}>
                Active Tier
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1.5 }}>
            <Card sx={{ border: `1px solid ${alpha(NEON, 0.2)}`, background: `linear-gradient(145deg, ${alpha(NEON, 0.09)} 0%, ${alpha(SURFACE_UP, 0.6)} 100%)` }}>
              <Box sx={{ px: 2.2, py: 1.8 }}>
                <FieldLabel>Sponsorship Amount</FieldLabel>
                <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, fontFamily: "'Rajdhani', sans-serif", color: NEON }}>{amount || '—'}</Typography>
              </Box>
            </Card>
            <Card sx={{ border: `1px solid ${alpha(NEON2, 0.2)}`, background: `linear-gradient(145deg, ${alpha(NEON2, 0.09)} 0%, ${alpha(SURFACE_UP, 0.6)} 100%)` }}>
              <Box sx={{ px: 2.2, py: 1.8 }}>
                <FieldLabel>Logo Size</FieldLabel>
                <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, fontFamily: "'Rajdhani', sans-serif", color: NEON2, textTransform: 'capitalize' }}>{tier.logo_size || 'Medium'}</Typography>
              </Box>
            </Card>
            <Card sx={{ border: `1px solid ${alpha(NEON3, 0.2)}`, background: `linear-gradient(145deg, ${alpha(NEON3, 0.09)} 0%, ${alpha(SURFACE_UP, 0.6)} 100%)` }}>
              <Box sx={{ px: 2.2, py: 1.8 }}>
                <FieldLabel>Description</FieldLabel>
                <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, fontFamily: "'Rajdhani', sans-serif", color: NEON3 }}>{tier.is_show_description ? 'Shown' : 'Hidden'}</Typography>
              </Box>
            </Card>
            <Card sx={{ border: `1px solid ${alpha(NEON, 0.2)}`, background: `linear-gradient(145deg, ${alpha(NEON, 0.09)} 0%, ${alpha(SURFACE_UP, 0.6)} 100%)` }}>
              <Box sx={{ px: 2.2, py: 1.8 }}>
                <FieldLabel>Contact Info</FieldLabel>
                <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, fontFamily: "'Rajdhani', sans-serif", color: NEON }}>{tier.is_show_contact ? 'Shown' : 'Hidden'}</Typography>
              </Box>
            </Card>
          </Box>
        </Box>

        {/* ── Benefit ── */}
        <Card delay={0.07} accent={NEON}>
          <CardHeader icon={DescriptionIcon} title="Benefit" />
          <Box sx={{ px: 2.5, py: 2 }}>
            <Box sx={{
              px: 2, py: 1.8, borderRadius: '10px',
              borderLeft: `3px solid ${alpha(NEON, 0.4)}`,
              background: alpha(SURFACE_TOP, 0.4),
            }}>
              <FieldValue
                muted={!tier.benefit}
                sx={{
                  fontSize: '0.92rem',
                  color: tier.benefit ? TEXT_BODY : alpha(TEXT_MUTED, 0.6),
                  lineHeight: 1.75,
                }}
              >
                {tier.benefit || '-'}
              </FieldValue>
            </Box>
          </Box>
        </Card>

        <Card delay={0.12} accent={NEON2}>
          <CardHeader icon={VisibilityIcon} title="Public page card preview" accent={NEON2} />
          <Box sx={{ px: 2.5, py: 2 }}>
            <PartnerPublicPreview
              compact
              tierDraft={{
                tier_name: tier.tier_name,
                logo_size: tier.logo_size,
                is_show_description: tier.is_show_description,
                is_show_contact: tier.is_show_contact,
              }}
            />
          </Box>
        </Card>

        {/* ── System info ── */}
        <SystemInformationSection
          createdBy={formatAuditUser(tier, 'created')}
          createdAt={tier.created_at ? new Date(tier.created_at).toLocaleString() : null}
          updatedBy={formatAuditUser(tier, 'updated')}
          updatedAt={tier.updated_at ? new Date(tier.updated_at).toLocaleString() : null}
          delay={0.18}
          accentColor={NEON}
          surfaceUp={SURFACE_UP}
          surfaceMid={SURFACE_MID}
          textBody={TEXT_BODY}
          textMuted={TEXT_MUTED}
        />
      </Box>

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={confirmDelete}
        loading={deleteLoading}
        title="Delete sponsor tier"
        itemName={tier?.tier_name}
        itemType="tier"
        description="You can only delete tiers that are not linked to any partner."
      />
    </Container>
  );
};

export default SponsorTierDetails;